import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Theme store for the "Dimension" design system.
 *
 * Three user-facing choices — light, dark, system — resolved down to two
 * concrete themes. The resolved theme is applied as a `dark` class on
 * <html>, which is what every CSS token in styles.css keys off of, so the
 * flip happens on the very next paint with no re-fetch and no remount.
 */

export type ThemeChoice = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "greentrack-theme";

/** Surface color per theme — used for <meta name="theme-color"> and the native status bar. */
const CANVAS: Record<ResolvedTheme, string> = {
  light: "#fafafa",
  dark: "#0a0a0a",
};

function systemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function readStoredChoice(): ThemeChoice {
  if (typeof localStorage === "undefined") return "light";
  const v = localStorage.getItem(THEME_STORAGE_KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "light";
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === "system" ? systemTheme() : choice;
}

/**
 * Writes the theme to the document. Kept as a standalone function so the
 * pre-hydration inline script in index.html can mirror the exact same logic.
 */
export function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
  root.dataset.theme = resolved;

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", CANVAS[resolved]);

  // Native shell (iOS/Android) — keep the status bar in step with the canvas.
  void syncNativeStatusBar(resolved);
}

type StatusBarPlugin = {
  setStyle?: (o: { style: string }) => Promise<void>;
  setBackgroundColor?: (o: { color: string }) => Promise<void>;
};

/**
 * Reaches the StatusBar plugin through the Capacitor runtime registry rather
 * than a static import, so the web bundle never depends on the native package.
 */
async function syncNativeStatusBar(resolved: ResolvedTheme) {
  const cap = (
    window as Window & {
      Capacitor?: {
        isNativePlatform?: () => boolean;
        Plugins?: Record<string, StatusBarPlugin>;
      };
    }
  ).Capacitor;

  if (!cap?.isNativePlatform?.()) return;
  const StatusBar = cap.Plugins?.StatusBar;
  if (!StatusBar) return;

  try {
    await StatusBar.setStyle?.({ style: resolved === "dark" ? "DARK" : "LIGHT" });
    await StatusBar.setBackgroundColor?.({ color: CANVAS[resolved] });
  } catch {
    /* plugin unavailable on this platform — web/PWA is fine without it */
  }
}


type ThemeContextValue = {
  /** What the user picked: light, dark, or follow-the-OS. */
  theme: ThemeChoice;
  /** What is actually on screen right now. */
  resolvedTheme: ResolvedTheme;
  setTheme: (choice: ThemeChoice) => void;
  /** Flip between light and dark, leaving "system" behind. */
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeChoice>(() => readStoredChoice());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(readStoredChoice()),
  );

  // Apply synchronously before paint so a change never shows a stale frame.
  useEffect(() => {
    const next = resolveTheme(theme);
    setResolvedTheme(next);
    applyTheme(next);
  }, [theme]);

  // Follow the OS live while the user is on "system".
  useEffect(() => {
    if (theme !== "system" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const next: ResolvedTheme = mq.matches ? "dark" : "light";
      setResolvedTheme(next);
      applyTheme(next);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  // Keep multiple tabs / the app and the site in sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY) setThemeState(readStoredChoice());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setTheme = useCallback((choice: ThemeChoice) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, choice);
    } catch {
      /* private mode — theme just won't persist */
    }
    setThemeState(choice);
    // Paint immediately rather than waiting on the effect.
    applyTheme(resolveTheme(choice));
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(resolveTheme(readStoredChoice()) === "dark" ? "light" : "dark");
  }, [setTheme]);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, toggleTheme }),
    [theme, resolvedTheme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  // Safe fallback for trees rendered outside the provider (e.g. isolated tests).
  return {
    theme: "light",
    resolvedTheme: typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark")
      ? "dark"
      : "light",
    setTheme: () => {},
    toggleTheme: () => {},
  };
}
